// Lists page rules. Words: LISTS_PAGE_WORDS.md. Mockup: MOCKUP_LISTS_APPROVED.html (approved 2026-10-01).
// Data names: safetag_config_copy.py
//   lists/equipmentClasses/items/{code}
//   lists/fieldLists/items/{list-slug--value-slug}
//   lists/plants/items/{siteNumber}
// Pure: no database. Shared by the page, the server steps (functions/src/lists.ts) and the tests.

import { PREFIX_LIST, PREFIX_NOTE, prefixOk, SKM_TYPES } from "./model-tag-prefixes";

// ---- What Firestore holds ----

export interface EquipmentClass {
  code: string;
  order: number | null;
  name: string;
  alsoAccepts: string[];
  nec11016Label: boolean;
  ieeeClass: string;
  card: string;
  devices: string[];
  rating: string;
  positionType: string;
  isActive: boolean;
  notes: string;
}

export interface FieldValue {
  id: string;
  listName: string;
  value: string;
  group: string;
  order: number | null;
  isActive: boolean;
  notes: string;
}

export interface Plant {
  siteNumber: string;
  name: string;
  city: string;
  state: string;
}

/** The first five sites. Plant checks use the live site list when they have it (Add Site, 2026-10-23). */
export const SITE_NUMBERS = ["00", "01", "02", "04", "10"];

// ---- Ids, as the config copy made them ----

export function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export function fieldValueId(listName: string, value: string): string {
  return `${slug(listName)}--${slug(value)}`;
}

// ---- Order ----

function byOrder<T extends { order: number | null }>(a: T, b: T, tie: (x: T, y: T) => number): number {
  const ao = a.order ?? Number.MAX_SAFE_INTEGER;
  const bo = b.order ?? Number.MAX_SAFE_INTEGER;
  return ao - bo || tie(a, b);
}

export function sortClasses(list: EquipmentClass[]): EquipmentClass[] {
  return [...list].sort((a, b) => byOrder(a, b, (x, y) => x.name.localeCompare(y.name)));
}

/**
 * In the Sheet, a value's order counts inside its group (Voltage: Low 1 to 13, Medium 1 to 20, High 1 to 6).
 * Groups follow the Sheet: Low, Medium, High first, then any other group by name.
 */
export const GROUP_ORDER = ["Low", "Medium", "High"];

export function groupRank(group: string): number {
  const k = GROUP_ORDER.indexOf(group);
  return k < 0 ? GROUP_ORDER.length : k;
}

export function sortValues(list: FieldValue[]): FieldValue[] {
  return [...list].sort(
    (a, b) =>
      groupRank(a.group) - groupRank(b.group) ||
      a.group.localeCompare(b.group) ||
      byOrder(a, b, (x, y) => x.value.localeCompare(y.value, "en", { numeric: true })),
  );
}

/** The values a move swaps among: same list, same group, in order. */
export function siblingsOf(values: FieldValue[], v: Pick<FieldValue, "listName" | "group">): FieldValue[] {
  return sortValues(values.filter((x) => x.listName === v.listName && x.group === v.group));
}

/** Where a new value lands: the end of its group. */
export function nextOrderInGroup(values: FieldValue[], listName: string, group: string): number {
  return Math.max(0, ...values.filter((x) => x.listName === listName && x.group === group).map((x) => x.order ?? 0)) + 1;
}

/**
 * Moves one item a step up or down, then numbers the whole list 1 to n.
 * Returns the new order for every item whose number changed, or null when it cannot move.
 */
export function moveOrder<T extends { order: number | null }>(sorted: T[], idOf: (x: T) => string, id: string, dir: "up" | "down"): Map<string, number> | null {
  const i = sorted.findIndex((x) => idOf(x) === id);
  const j = dir === "up" ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= sorted.length) return null;
  const next = [...sorted];
  [next[i], next[j]] = [next[j]!, next[i]!];
  const out = new Map<string, number>();
  next.forEach((x, k) => {
    if (x.order !== k + 1) out.set(idOf(x), k + 1);
  });
  return out;
}

/**
 * Drag and drop (LISTS_DRAG_B3_WORDS.md): the whole new order for one list, saved once on Done. Numbers it 1 to n.
 * Returns the new order for every item whose number changed, or null when `ids` is not exactly the same items.
 */
export function orderFromIds<T extends { order: number | null }>(sorted: T[], idOf: (x: T) => string, ids: readonly string[]): Map<string, number> | null {
  const have = sorted.map(idOf);
  if (ids.length !== have.length || new Set(ids).size !== ids.length || ids.some((id) => !have.includes(id))) return null;
  const out = new Map<string, number>();
  ids.forEach((id, k) => {
    if (sorted.find((x) => idOf(x) === id)!.order !== k + 1) out.set(id, k + 1);
  });
  return out;
}

// ---- Field lists ----

export interface ListSummary {
  listName: string;
  count: number;
  active: number;
}

/** The lists in Sheet order: by the first value's place in the copy, so Voltage leads. */
export function listSummaries(values: FieldValue[], sheetOrder: string[] = []): ListSummary[] {
  const map = new Map<string, ListSummary>();
  for (const v of values) {
    const s = map.get(v.listName) ?? { listName: v.listName, count: 0, active: 0 };
    s.count += 1;
    if (v.isActive) s.active += 1;
    map.set(v.listName, s);
  }
  const rank = (n: string) => {
    const k = sheetOrder.indexOf(n);
    return k < 0 ? Number.MAX_SAFE_INTEGER : k;
  };
  return [...map.values()].sort((a, b) => rank(a.listName) - rank(b.listName) || a.listName.localeCompare(b.listName));
}

export interface ValueGroup {
  group: string;
  values: FieldValue[];
}

/** One list's values in order, under their groups. Groups keep the order their first value has. */
export function groupValues(values: FieldValue[], listName: string): ValueGroup[] {
  const groups: ValueGroup[] = [];
  for (const v of sortValues(values.filter((x) => x.listName === listName))) {
    const g = groups.find((x) => x.group === v.group);
    if (g) g.values.push(v);
    else groups.push({ group: v.group, values: [v] });
  }
  return groups;
}

export function groupsOf(values: FieldValue[], listName: string): string[] {
  return [...new Set(sortValues(values.filter((x) => x.listName === listName)).map((v) => v.group).filter(Boolean))];
}

export interface FieldValueInput {
  value: string;
  group: string;
  notes: string;
  isActive: boolean;
}

export function fieldValueProblem(input: FieldValueInput, all: FieldValue[], listName: string, exceptId = ""): string | null {
  const v = input.value.trim();
  if (!v) return "Enter the value.";
  if (v.length > 80) return "Keep the value under 80 characters.";
  if (input.group.trim().length > 60) return "Keep the group under 60 characters.";
  if (input.notes.trim().length > 300) return "Keep the note under 300 characters.";
  const dupe = all.find((x) => x.listName === listName && x.id !== exceptId && x.value.trim().toLowerCase() === v.toLowerCase());
  if (dupe) return `${dupe.value} is already in ${listName}.`;
  if (!exceptId && !slug(v)) return "Use at least one letter or number in the value.";
  return null;
}

// ---- Equipment classes ----

export interface ClassInput {
  name: string;
  alsoAccepts: string;
  nec11016Label: boolean;
  ieeeClass: string;
  card: string;
  devices: string;
  rating: string;
  positionType: string;
  notes: string;
}

export function splitList(s: string, upper = false): string[] {
  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => (upper ? x.toUpperCase() : x));
}

export function classProblem(input: ClassInput, all: EquipmentClass[], code: string): string | null {
  const name = input.name.trim();
  if (!name) return "Enter the class name.";
  if (name.length > 60) return "Keep the name under 60 characters.";
  const dupe = all.find((c) => c.code !== code && c.name.trim().toLowerCase() === name.toLowerCase());
  if (dupe) return `${dupe.name} is already class ${dupe.code}.`;
  for (const a of splitList(input.alsoAccepts, true)) {
    if (!/^[A-Z0-9]{1,8}$/.test(a)) return `"${a}" is not a code. Codes are letters and numbers, up to 8.`;
    const owner = all.find((c) => c.code !== code && (c.code === a || c.alsoAccepts.includes(a)));
    if (owner) return `${a} already points to ${owner.name}.`;
  }
  for (const [label, v] of [
    ["IEEE class", input.ieeeClass],
    ["Card", input.card],
    ["Devices", input.devices],
    ["Rating", input.rating],
    ["Position type", input.positionType],
  ] as const) {
    if (v.trim().length > 120) return `Keep ${label.toLowerCase()} under 120 characters.`;
  }
  if (input.notes.trim().length > 300) return "Keep the note under 300 characters.";
  return null;
}

export function classFieldsFrom(input: ClassInput): Omit<EquipmentClass, "code" | "order" | "isActive"> {
  return {
    name: input.name.trim(),
    alsoAccepts: splitList(input.alsoAccepts, true),
    nec11016Label: input.nec11016Label,
    ieeeClass: input.ieeeClass.trim(),
    card: input.card.trim(),
    devices: splitList(input.devices),
    rating: input.rating.trim(),
    positionType: input.positionType.trim(),
    notes: input.notes.trim(),
  };
}

const CLASS_LABELS: Record<string, string> = {
  name: "Name",
  alsoAccepts: "Also accepts",
  nec11016Label: "NEC 110.16 label",
  ieeeClass: "IEEE class",
  card: "Card",
  devices: "Devices",
  rating: "Rating",
  positionType: "Position type",
  notes: "Notes",
};

function show(v: unknown): string {
  if (Array.isArray(v)) return v.length ? v.join(", ") : "none";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return v === "" || v === null || v === undefined ? "blank" : String(v);
}

/** What the confirm lists: each field that changes, from and to. */
export function classChanges(current: EquipmentClass, input: ClassInput): string[] {
  const next = classFieldsFrom(input);
  const out: string[] = [];
  for (const k of Object.keys(CLASS_LABELS) as (keyof typeof next)[]) {
    if (show(current[k]) !== show(next[k])) out.push(`${CLASS_LABELS[k]} changes from "${show(current[k])}" to "${show(next[k])}".`);
  }
  return out;
}

// ---- Plants ----

export interface PlantInput {
  siteNumber: string;
  name: string;
  city: string;
  state: string;
}

export function plantProblem(input: PlantInput, all: Plant[], oldNumber = "", sites: readonly string[] = SITE_NUMBERS): string | null {
  const n = input.siteNumber.trim();
  if (!/^\d{2}$/.test(n)) return "The plant number is two digits, like 07.";
  if (sites.includes(n)) return `${n} is already a site. Plants are the ones not yet sites.`;
  if (n !== oldNumber && all.some((p) => p.siteNumber === n)) return `Plant ${n} is already on the list.`;
  if (!input.name.trim()) return "Enter the plant name.";
  if (input.name.trim().length > 60) return "Keep the name under 60 characters.";
  if (input.city.trim().length > 60) return "Keep the city under 60 characters.";
  if (input.state.trim() && !/^[A-Za-z]{2}$/.test(input.state.trim())) return "State is the two-letter code, like KY.";
  return null;
}

export function sortPlants(list: Plant[]): Plant[] {
  return [...list].sort((a, b) => a.siteNumber.localeCompare(b.siteNumber));
}

/** The 24 field lists in Sheet order (Field Lists tab, 2026-10-01). A list added later sorts after these. */
export const FIELD_LIST_ORDER = [
  "Voltage", "Amps", "Frame Amps", "Trip Type", "Fuse Class", "Fuse Type", "Winding Connection", "Electrode Configuration",
  "Gap Source", "Conductor Size", "Conductor Material", "Insulation Type", "Conductors per Phase", "Raceway Type", "Raceway Size",
  "Bucket Type", "Section Type", "WCR Basis", "Transition", "Input Protection", "Panel Breaker Status", "Optional Field",
  "Raceway Material", "Cable Construction",
];

// ---- Model Tag Prefixes (AF_MAPPING_WORDS.md section 5, Robert I10 2026-10-21) ----
// A Field List like the others: Value = the prefix, Group = the SKM Type. It starts as today's prefixes.
// Until the first edit it lives only in the app, so the page shows the defaults; the first save writes them.


/** Today's prefixes as Field List values, in the order the types are listed. */
export function defaultPrefixValues(): FieldValue[] {
  return SKM_TYPES.map((t, i) => ({ id: fieldValueId(PREFIX_LIST, t.prefix), listName: PREFIX_LIST, value: t.prefix, group: t.type, order: i + 1, isActive: true, notes: PREFIX_NOTE }));
}

/** The page's values, with the prefix list filled from the defaults when nobody has saved it yet. */
export function withPrefixList(values: FieldValue[]): FieldValue[] {
  return values.some((v) => v.listName === PREFIX_LIST) ? values : [...values, ...defaultPrefixValues()];
}

/**
 * A change to the prefix list. Only an existing type's letters change: no adds, and the type (Group)
 * stays. `seed` lists the defaults to write first when the list is not stored yet.
 */
export function prefixEdit(stored: FieldValue[], id: string, input: FieldValueInput): { problem: string } | { seed: FieldValue[]; value: string; group: string } {
  const seed = stored.length ? [] : defaultPrefixValues();
  const list = stored.length ? stored : seed;
  if (!id) return { problem: "A new SKM type needs a build. Change the letters of an existing type." };
  const current = list.find((v) => v.id === id);
  if (!current) return { problem: `That value is not in ${PREFIX_LIST}.` };
  const value = input.value.trim().toUpperCase();
  if (!prefixOk(value)) return { problem: "Use 2 to 6 capital letters or digits, starting with a letter. MTRL, XF2." };
  const dupe = list.find((v) => v.id !== id && v.value.trim().toUpperCase() === value);
  if (dupe) return { problem: `${value} is already the prefix for ${dupe.group}. Two types cannot share letters.` };
  return { seed, value, group: current.group };
}
